import { styled } from "styled-components";

import { MagnifyingGlassIcon } from "@vericus/cadmus-icons";
import {
  DatePickerInput,
  type DatePickerValue,
  type DatesRangeValue,
  desktop,
  DropdownMenu,
  Input,
  SelectButton,
  Text,
} from "@vericus/cadmus-ui";

import { useAppDispatch, useAppSelector } from "@/data/hooks";
import {
  IPastAssessmentFilters,
  reduxDateDeserialiser,
  SerializedDateRangePickerValue,
  updatePastAssessmentsFilter,
} from "@/data/templates";
import { selectPastAssessmentFilters } from "@/data/templates/selectors";
import {
  AssessmentType,
  SubjectFragment,
  TaskFormat,
} from "@/generated/graphql";

export interface PastAssessmentsFiltersProps {
  /**
   * Whether the exams feature is enabled.
   */
  areExamsEnabled: boolean;
  /**
   * The subjects that the user is able to select for filtering
   */
  subjects: SubjectFragment[];
}

/**
 * A set of filters for the Past Assessments component.
 */
export const PastAssessmentsFilters = ({
  areExamsEnabled,
  subjects,
}: PastAssessmentsFiltersProps) => {
  const pastAssessmentFilters = useAppSelector(selectPastAssessmentFilters);
  const dispatch = useAppDispatch();

  const [startDateValue, endDateValue] = pastAssessmentFilters.dueDateRange ?? [
    null,
    null,
  ];

  const datePickerRangeValue: DatePickerValue<"range"> = [
    startDateValue ? reduxDateDeserialiser(startDateValue) : null,
    endDateValue ? reduxDateDeserialiser(endDateValue) : null,
  ];

  return (
    <FilterContainer>
      <DropdownMenu.Root>
        <DropdownMenu.Trigger asChild>
          <SelectButton>
            {pastAssessmentFilters.subject?.name ?? "Subject"}
          </SelectButton>
        </DropdownMenu.Trigger>
        <DropdownMenu.Content>
          {subjects.map((subject) => (
            <StyledMenuItem
              key={subject.id}
              onClick={() => {
                dispatch(
                  updatePastAssessmentsFilter({
                    ...pastAssessmentFilters,
                    subject: subject,
                  })
                );
              }}
            >
              <Text
                key={`${subject.id}-child`}
                kind="systemMd"
                textAlign={"left"}
              >
                {subject.name}
              </Text>
            </StyledMenuItem>
          ))}
        </DropdownMenu.Content>
      </DropdownMenu.Root>
      {areExamsEnabled && (
        <DropdownMenu.Root>
          <DropdownMenu.Trigger asChild>
            <SelectButton>
              {assessmentTypeSelectorOptions.find(
                (option) =>
                  option.value === pastAssessmentFilters.assessmentType
              )?.display ?? "Type"}
            </SelectButton>
          </DropdownMenu.Trigger>
          <DropdownMenu.Content>
            {assessmentTypeSelectorOptions.map((option) => (
              <StyledMenuItem
                key={option.display}
                onSelect={() => {
                  dispatch(
                    updatePastAssessmentsFilter({
                      ...pastAssessmentFilters,
                      assessmentType: option.value,
                    })
                  );
                }}
              >
                <Text kind="systemMd" textAlign={"left"}>
                  {option.display}
                </Text>
              </StyledMenuItem>
            ))}
          </DropdownMenu.Content>
        </DropdownMenu.Root>
      )}
      <DropdownMenu.Root>
        <DropdownMenu.Trigger asChild>
          <SelectButton>
            {taskFormatSelectorOptions.find(
              (option) => option.value === pastAssessmentFilters.taskFormat
            )?.display ?? "Format"}
          </SelectButton>
        </DropdownMenu.Trigger>
        <DropdownMenu.Content>
          {taskFormatSelectorOptions.map((option) => (
            <StyledMenuItem
              key={option.display}
              onSelect={() => {
                dispatch(
                  updatePastAssessmentsFilter({
                    ...pastAssessmentFilters,
                    taskFormat: option.value,
                  })
                );
              }}
            >
              <Text kind="systemMd" textAlign={"left"}>
                {option.display}
              </Text>
            </StyledMenuItem>
          ))}
        </DropdownMenu.Content>
      </DropdownMenu.Root>
      <DatePickerInput
        type="range"
        data-testid={"dueDateRangePicker"}
        allowSingleDateInRange
        popoverProps={{
          withinPortal: false,
        }}
        placeholder={
          pastAssessmentFilters.assessmentType === AssessmentType.Exam
            ? "Exam date range"
            : "Due date range"
        }
        value={datePickerRangeValue}
        onChange={(selectedValues: DatesRangeValue) => {
          dispatch(
            updatePastAssessmentsFilter({
              ...pastAssessmentFilters,
              dueDateRange: selectedValues.map((selectedValue) =>
                selectedValue ? new Date(selectedValue).getTime() : null
              ) as SerializedDateRangePickerValue,
            })
          );
        }}
      />

      <Input
        placeholder="Assessment name"
        fullWidth
        value={pastAssessmentFilters.assessmentNameQuery || ""}
        onChange={(event) => {
          const value = event.target.value;
          dispatch(
            updatePastAssessmentsFilter({
              ...pastAssessmentFilters,
              assessmentNameQuery: value,
            })
          );
        }}
        leftSection={<MagnifyingGlassIcon label="Search" size={22} />}
      />
    </FilterContainer>
  );
};

type AssessmentTypeOption = {
  display: string;
  value: Exclude<IPastAssessmentFilters["assessmentType"], undefined>;
};

const assessmentTypeSelectorOptions = [
  { display: "All", value: "all" },
  { display: "Assignments", value: AssessmentType.Assignment },
  { display: "Exams", value: AssessmentType.Exam },
] satisfies AssessmentTypeOption[];

type TaskFormatOption = {
  display: string;
  value: Exclude<IPastAssessmentFilters["taskFormat"], undefined>;
};

const taskFormatSelectorOptions = [
  { display: "All", value: "all" },
  { display: "Multi-format", value: TaskFormat.Multiformat },
  { display: "Classic", value: TaskFormat.Classic },
] satisfies TaskFormatOption[];

const FilterContainer = styled.div`
  padding-top: 24px;
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  & > * {
    flex-basis: 106px;
    flex-grow: 1;
  }

  margin-bottom: 24px;
  ${desktop`
    margin-bottom: 0;
  `}
`;

const StyledMenuItem = styled(DropdownMenu.Item)`
  padding: 7px 16px;
`;
